import type { z } from 'zod';

import { getApiConfig } from '~/config';
import { auth } from '~/config/firebase';
import { getActiveOrgId } from '~/utils/activeOrg';
import { logger } from '~/utils/logger';

import { ERROR_MESSAGES } from './config';

// Error types
interface ApiErrorDetails {
  contentType?: string;
  validationErrors?: z.core.$ZodIssue[];
  responseStatus?: number;
  responseError?: unknown;
}

// Response types
export interface ApiResponse<T> {
  data: T;
}

export class ApiError extends Error {
  constructor(
    message: string,
    public status?: number,
    public details?: ApiErrorDetails | unknown,
    public contentType?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

// Error messages
const API_ERROR_MESSAGES = {
  ...ERROR_MESSAGES,
  INVALID_RESPONSE: 'Invalid API response format',
  INVALID_CONTENT_TYPE: 'Invalid content type',
} as const;

// Helper function to build URL
function buildUrl(endpoint: string): string {
  const config = getApiConfig();
  const baseURL = config.baseURL.endsWith('/') ? config.baseURL.slice(0, -1) : config.baseURL;
  const path = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;

  return `${baseURL}${path}`;
}

/**
 * Extract error message from backend response data.
 * FastAPI uses 'detail', others may use 'message'.
 */
function extractErrorMessage(data: unknown): string {
  if (data && typeof data === 'object') {
    if ('detail' in data) {
      if (typeof data.detail === 'string') return data.detail;
      // Handle nested object: {detail: {message: "...", error: "..."}}
      if (typeof data.detail === 'object' && data.detail !== null) {
        const nested = data.detail as Record<string, unknown>;
        if (typeof nested['message'] === 'string') return nested['message'];
        if (typeof nested['error'] === 'string') return nested['error'];
      }
    }
    if ('message' in data && typeof data.message === 'string') return data.message;
  }
  return '';
}

/**
 * Get Firebase ID token for authenticated requests
 * Returns null if user is not authenticated
 */
async function getAuthToken(): Promise<string | null> {
  const user = auth.currentUser;
  if (!user) {
    return null;
  }

  try {
    return await user.getIdToken();
  } catch (error) {
    logger.error(
      { component: 'ApiClient' },
      'Failed to get Firebase ID token',
      error instanceof Error ? error : new Error(String(error)),
    );
    return null;
  }
}

/**
 * Get appropriate default error message based on HTTP status code.
 * Session expiration detection is handled by checking backend error messages,
 * so we don't need to check the URL here.
 */
function getDefaultErrorMessage(status: number): string {
  if (status >= 500) {
    return ERROR_MESSAGES.SERVER_ERROR;
  } else if (status === 404) {
    return ERROR_MESSAGES.NOT_FOUND;
  } else if (status === 401) {
    return ERROR_MESSAGES.UNAUTHORIZED;
  } else if (status === 400) {
    return ERROR_MESSAGES.VALIDATION_ERROR;
  } else if (status >= 400) {
    return `Client error (${status})`;
  } else {
    return `Unexpected error (${status})`;
  }
}

// Helper function to handle API response
async function handleResponse<T>(response: Response): Promise<ApiResponse<T>> {
  const contentType = response.headers.get('content-type');
  const url = response.url;

  // Handle 204 No Content responses (no body expected)
  if (response.status === 204) {
    return { data: undefined as unknown as T };
  }

  // Parse JSON response with error handling
  let data: unknown;
  try {
    data = await response.json();
  } catch (parseError) {
    logger.error(
      { component: 'ApiClient' },
      `Failed to parse JSON response from ${url}`,
      parseError instanceof Error ? parseError : new Error(String(parseError)),
      { status: response.status, contentType },
    );
    throw new ApiError(
      API_ERROR_MESSAGES.INVALID_RESPONSE,
      response.status,
      {
        parseError: parseError instanceof Error ? parseError.message : String(parseError),
        contentType,
      },
      contentType || undefined,
    );
  }

  // Handle error responses
  if (!response.ok) {
    const backendMessage = extractErrorMessage(data);
    const isSessionExpired =
      backendMessage.toLowerCase().includes('session') &&
      (backendMessage.toLowerCase().includes('expired') ||
        backendMessage.toLowerCase().includes('not found'));

    // Determine final error message: prioritize session expiration, then backend message, then default
    const finalErrorMessage = isSessionExpired
      ? ERROR_MESSAGES.SESSION_EXPIRED
      : backendMessage || getDefaultErrorMessage(response.status);

    // Log different error types with appropriate levels
    if (response.status >= 500) {
      // Server errors - these are critical and should be tracked
      logger.error(
        { component: 'ApiClient' },
        `Server error (${response.status}): ${finalErrorMessage}`,
        new Error(finalErrorMessage),
        { status: response.status, data, url: response.url },
      );
    } else if (response.status >= 400) {
      // Client errors - these are expected in normal operation
      logger.warn(
        { component: 'ApiClient' },
        `Client error (${response.status}): ${finalErrorMessage}`,
        { status: response.status, data, url: response.url },
      );
    }

    throw new ApiError(finalErrorMessage, response.status, data);
  }

  // Detect if response is already wrapped (some endpoints return { data: ... })
  if (data && typeof data === 'object' && 'data' in data && !Array.isArray(data)) {
    return data as ApiResponse<T>;
  }
  return { data: data as T };
}

// Track if API is known to be down - prevents retry spam
let apiKnownDown = false;
let apiDownSince: number | null = null;

function isAbortLikeTimeout(error: unknown): boolean {
  if (!(error instanceof Error)) {
    return false;
  }

  const timeoutErrorNames = new Set(['AbortError', 'TimeoutError']);
  if (timeoutErrorNames.has(error.name)) {
    return true;
  }

  // Some runtimes wrap timeout aborts with generic names but timeout-like messages.
  const message = error.message.toLowerCase();
  return message.includes('aborted') || message.includes('timeout');
}

// Helper function to handle retries - NO retries when API is known to be down
async function withRetry<T>(operation: () => Promise<T>): Promise<T> {
  // If API is known to be down, fail immediately - don't spam
  if (apiKnownDown) {
    throw new ApiError(API_ERROR_MESSAGES.NETWORK_ERROR, undefined, {
      reason: 'API marked as unavailable',
      downSince: apiDownSince,
    });
  }

  try {
    return await operation();
  } catch (error) {
    // On network error, mark API as down and fail immediately
    // Let React Query's recovery mechanism handle retry
    if (error instanceof Error && !(error instanceof ApiError)) {
      logger.warn({ component: 'ApiClient' }, 'Network error - marking API as down', {
        error: error.message,
      });
      markApiDown();

      throw new ApiError(API_ERROR_MESSAGES.NETWORK_ERROR, undefined, {
        originalError: error.message,
      });
    }

    throw error;
  }
}

// For testing only - reset module state between tests
export function __resetApiState(): void {
  apiKnownDown = false;
  apiDownSince = null;
}

// API request function
export async function apiRequest<T = unknown>(
  endpoint: string,
  options: RequestInit & { timeout?: number } = {},
): Promise<ApiResponse<T>> {
  const config = getApiConfig();
  const url = buildUrl(endpoint);

  // Use custom timeout if provided, otherwise use config timeout
  // PRD generation endpoints can take 15-20 seconds due to AI processing
  const timeout = options.timeout ?? config.timeout;

  // Get Firebase auth token
  const authToken = await getAuthToken();

  const defaultHeaders: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  };

  // Add Authorization header if user is authenticated
  if (authToken) {
    defaultHeaders['Authorization'] = `Bearer ${authToken}`;
  }

  // Add X-Organization-ID header for multi-org support
  const activeOrgId = getActiveOrgId();
  if (activeOrgId) {
    defaultHeaders['X-Organization-ID'] = activeOrgId;
  }

  // Remove timeout from options to avoid passing it to fetch
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { timeout: _, ...fetchOptions } = options;

  const requestOptions: RequestInit = {
    ...fetchOptions,
    headers: {
      ...defaultHeaders,
      ...fetchOptions.headers,
    },
  };

  logger.info(
    { component: 'ApiClient' },
    `Making ${options.method || 'GET'} request to ${endpoint}`,
    { url, method: options.method || 'GET', timeout },
  );

  return withRetry(async () => {
    try {
      const timeoutSignal = AbortSignal.timeout(timeout);
      const response = await fetch(url, {
        ...requestOptions,
        ...(timeoutSignal && { signal: timeoutSignal }),
      });

      return handleResponse<T>(response);
    } catch (error) {
      if (error instanceof ApiError) {
        // ApiError is already logged in handleResponse
        throw error;
      }

      // Handle timeout errors explicitly
      if (isAbortLikeTimeout(error)) {
        logger.warn({ component: 'ApiClient' }, `Request timeout after ${timeout}ms for ${url}`, {
          timeout,
          url,
          method: requestOptions.method || 'GET',
        });
        throw new ApiError(`Request timeout after ${timeout}ms`, 408, { timeout, url });
      }

      // Handle network errors - let retry logic handle them first
      if (error instanceof Error) {
        logger.error({ component: 'ApiClient' }, `Network error while accessing ${url}`, error, {
          method: requestOptions.method || 'GET',
        });

        // Throw the original error so retry logic can handle it
        throw error;
      }

      logger.error(
        { component: 'ApiClient' },
        `Unknown error while accessing ${url}`,
        new Error('Unknown error'),
        { error },
      );
      throw error;
    }
  });
}

export function markApiDown(): void {
  apiKnownDown = true;
  apiDownSince = Date.now();
}

export function markApiUp(): void {
  apiKnownDown = false;
  apiDownSince = null;
}
